import {
  DecisionPageLayout,
  DecisionSection,
  DecisionSubHeading,
  DecisionList,
  InlineRelatedLinks,
} from "@/components/DecisionPageLayout";
import { Link } from "@/lib/router-compat";

const seo = {
  title: "How Soon Can I Be Seen? | Neuro Wellness TMS",
  description:
    "Wondering how quickly you can start treatment? Learn about consultation scheduling, insurance verification timelines, and what to expect.",
  canonical: "/how-soon-can-i-be-seen",
  keywords:
    "how soon can I be seen TMS, TMS consultation scheduling, SAINT treatment timeline, depression treatment wait time Florida",
};

const relatedLinks = [
  {
    title: "What to Expect From Your Consultation",
    href: "/what-to-expect-from-your-consultation",
  },
  { title: "Insurance Verification", href: "/how-insurance-works" },
  { title: "Schedule a Consultation", href: "/contactus" },
  { title: "Am I a Candidate?", href: "/am-i-a-candidate" },
];

export default function HowSoonCanIBeSeen() {
  return (
    <DecisionPageLayout
      seo={seo}
      badge="Scheduling"
      title="How Soon Can I Be Seen?"
      subtitle="If you're ready to explore treatment, here's what the timeline looks like from first call to first session."
    >
      <DecisionSection heading="The short answer">
        <p>
          A phone consultation can typically be scheduled within a few days of
          your first contact. The full timeline from consultation to first
          treatment session depends on insurance verification, prior
          authorization, and — for SAINT — scheduling an fMRI scan.
        </p>
      </DecisionSection>

      <DecisionSection heading="The timeline, step by step">
        <DecisionList
          items={[
            "Step 1: Phone consultation — typically within a few days of calling",
            "Step 2: Insurance verification — usually 3–10 business days, depending on your plan",
            "Step 3: Prior authorization (if required) — a few days to two weeks",
            "Step 4: Scheduling — once authorization is confirmed, we schedule your first session",
            "For SAINT: an fMRI scan is needed before treatment, adding a few days",
          ]}
        />
        <p>
          For standard TMS, treatment can often begin within 1–3 weeks of your
          first call, depending on insurance turnaround. For SAINT, the timeline
          is similar but includes the fMRI step.
        </p>
      </DecisionSection>

      <DecisionSection heading="What you can do to speed things up">
        <DecisionList
          items={[
            "Have your insurance card ready when you call",
            "Prepare a list of antidepressants you've tried, with approximate dates",
            "Gather records from your current prescriber if available",
            "Complete the insurance verification form before your consultation",
          ]}
        />
      </DecisionSection>

      <DecisionSection heading="If you're in crisis">
        <p>
          If you're in crisis or need someone to talk to right now, call or text
          988 any time — the Suicide and Crisis Lifeline is available 24/7. If
          you're in immediate danger, call 911.
        </p>
      </DecisionSection>

      <DecisionSection heading="Start now">
        <p>
          Call{" "}
          <a
            href="tel:9548279793"
            className="text-accent font-semibold hover:underline"
          >
            (954) 827-9793
          </a>{" "}
          or{" "}
          <Link to="/contactus" className="text-accent hover:underline">
            schedule a consultation
          </Link>
          .
        </p>
      </DecisionSection>

      <InlineRelatedLinks heading="Related Pages" links={relatedLinks} />
    </DecisionPageLayout>
  );
}
